import { styled } from '@linaria/react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { AgentMinerEvent } from '../../../api/agent-miner-client/agent-miner-client.type';
import {
  inkDarkest,
  inkLight,
  inkLighter,
  secondaryBase,
  secondaryDarkest,
  secondaryLightest,
  skyBase,
  skyLight,
  skyLightest,
} from '../../../styles/design-tokens';
import { fmtDuration, fmtNum } from './formatters';

const REMARK_PLUGINS = [remarkGfm];

/**
 * Serialises an arbitrary value to a human-readable string for display.
 * Strings are returned as-is; objects are pretty-printed as JSON; nullish values become `"(none)"`.
 * @param {unknown} value - The value to format.
 * @returns {string} A display-safe string representation of the value.
 */
const formatJson = (value: unknown): string => {
  if (value == null) return '(none)';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

/**
 * Longest single value or label this summary will inline. Every branch below
 * routes through {@link clipSummaryValue} or {@link sanitizeSummaryLabel},
 * because these summaries render into a pill with no scroll container of its
 * own — and an `image` content block's base64 `source` averages ~78 KB.
 */
const SUMMARY_VALUE_MAX_LENGTH = 60;

/** Tighter budget for the collapsed tool signature, which shares one line. */
const ARG_PREVIEW_MAX_LENGTH = 40;

/**
 * Truncates a value to {@link SUMMARY_VALUE_MAX_LENGTH} with an ellipsis.
 *
 * Splits by code point rather than `slice`, which cuts by UTF-16 unit and can
 * leave a lone surrogate — that reaches the accessible name as a replacement
 * character rather than as text.
 */
const clipSummaryValue = (value: string, maxLength = SUMMARY_VALUE_MAX_LENGTH): string => {
  const points = Array.from(value);
  return points.length > maxLength ? `${points.slice(0, maxLength).join('')}…` : value;
};

/**
 * Renders a label position — a content-block `type` or an object key — safely.
 *
 * These sit where the summary's own grammar lives, so raw wire text there can
 * impersonate it: a crafted `type` of `x") · Bash(cmd="y` turns one tool row
 * into what reads as two. `type` and keys are identifiers by contract, so
 * anything outside an identifier charset is dropped rather than escaped, and
 * the result is clipped like any other value. Values keep their punctuation —
 * mangling a genuine tool result would cost more than it buys — so a value can
 * still contain a separator, and that residual is documented rather than fixed.
 *
 * @param {string} label - A `type` or object key from an unvalidated payload.
 * @returns {string} The label reduced to identifier characters and clipped.
 */
const sanitizeSummaryLabel = (label: string): string => clipSummaryValue(label.replace(/[^\w.\-/]+/g, ' ').trim());

/**
 * Summarises one element of an array payload.
 *
 * Array `tool_output`s are Anthropic content blocks in practice — `text` and
 * `input_text` carry `{ type, text }`, while `image` carries `{ type, source }`
 * and has no `text` key at all. So the type is shown always and the text only
 * when it exists, which keeps an image's base64 out of the summary entirely
 * rather than relying on truncation to hide it.
 *
 * @param {unknown} element - One element of an array payload.
 * @returns {string} A short, bounded description of the element.
 */
const summarizeArrayElement = (element: unknown): string => {
  if (typeof element === 'string') return clipSummaryValue(element);
  const keyed = asKeyedObject(element);
  if (!keyed) return clipSummaryValue(JSON.stringify(element) ?? String(element));
  const type = typeof keyed.type === 'string' ? keyed.type : null;
  const text = typeof keyed.text === 'string' ? keyed.text : null;
  if (type && text) return `${sanitizeSummaryLabel(type)}: ${clipSummaryValue(text)}`;
  if (type) return sanitizeSummaryLabel(type);
  return clipSummaryValue(JSON.stringify(keyed) ?? 'item');
};

/**
 * Describes the SHAPE of a tool payload that has no key/value preview.
 *
 * Used for the collapsed tool signature, where the goal is to say that
 * arguments exist without promoting unvalidated wire text into the line's own
 * `(`/`)`/`·` grammar — a crafted payload could otherwise read as a second tool
 * call in what is an audit view. Primitives are rendered faithfully because
 * they are unambiguous and short; strings and arrays get a bounded descriptor.
 *
 * @param {unknown} value - A payload that is not a keyed object.
 * @returns {string | null} A short descriptor, or null when there is nothing to say.
 */
const describeArgShape = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string')
    return value.length === 0 ? '""' : `"${clipSummaryValue(value, ARG_PREVIEW_MAX_LENGTH)}"`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return value.length === 1 ? '1 item' : `${value.length} items`;
  return clipSummaryValue(JSON.stringify(value) ?? String(value), ARG_PREVIEW_MAX_LENGTH);
};

/**
 * Produces a compact inline summary of an arbitrary tool payload.
 *
 * Handles all three shapes the backend actually stores in these JSONFields —
 * keyed object, bare string, and array — rather than only the first. Narrowing
 * to keyed objects alone silently turned every array result into `"ok"`, which
 * discarded the payload of every MCP tool result on screen.
 *
 * @param {unknown} record - The raw payload to summarise.
 * @param {number} [limit=3] - Maximum number of entries or elements to include.
 * @returns {string} A dot-separated summary, or `"ok"` when there is nothing to show.
 */
const summarizeRecord = (record: unknown, limit = 3): string => {
  if (typeof record === 'string') return record.trim() ? clipSummaryValue(record) : 'ok';
  if (Array.isArray(record)) {
    if (record.length === 0) return 'ok';
    const shown = record.slice(0, limit).map(summarizeArrayElement).join(' · ');
    const hidden = record.length - Math.min(limit, record.length);
    return hidden > 0 ? `${shown} · +${hidden} more` : shown;
  }
  const keyed = asKeyedObject(record);
  if (!keyed) return 'ok';
  const entries = Object.entries(keyed).slice(0, limit);
  if (entries.length === 0) return 'ok';
  return entries
    .map(
      ([k, v]) =>
        `${sanitizeSummaryLabel(k)}: ${clipSummaryValue(typeof v === 'string' ? v : (JSON.stringify(v) ?? String(v)))}`,
    )
    .join(' · ');
};

/**
 * Matches content that is ENTIRELY one XML-style envelope — an opening tag whose
 * matching closing tag ends the message.
 *
 * Agent runtimes inject these as user messages: `<task-notification>`,
 * `<system-reminder>`, `<command-name>`, `<local-command-stdout>`,
 * `<cross-session-message>`. They are machine text, not Markdown — with no blank
 * line inside, CommonMark parses the whole message as a single HTML block, so the
 * Markdown path collapses their one-tag-per-line structure onto a single line.
 *
 * Deliberately stricter than testing the first line alone. A message that merely
 * STARTS with an envelope and then continues into real prose — a `<command-name>`
 * header above a typed request — keeps the Markdown path so its headings, lists
 * and tables still format. Both paths preserve every character; this only picks
 * which one reads better.
 *
 * The closing tag is NOT matched back to the opening one, because the common
 * Claude Code slash-command shape is a run of SIBLINGS rather than one wrapper:
 * `<command-name>…</command-name>` then `<command-message>…</command-message>`
 * then `<command-args>…</command-args>`. Requiring a matching pair missed all of
 * those. Known cost of the looser rule: content that merely opens and closes with
 * tags while holding prose in between (`<p>a</p>` … prose … `<p>b</p>`) renders
 * verbatim, so its Markdown stays literal. Measured at zero occurrences against
 * this app's real transcripts, and lossless either way, so it is accepted.
 */
const WHOLE_ENVELOPE = /^<[a-zA-Z][a-zA-Z0-9-]*(?:\s[^>]*)?>[\s\S]*<\/[a-zA-Z][a-zA-Z0-9-]*>$/;

/**
 * Renders message text inside a styled bubble.
 *
 * Responsibilities:
 * 1. Fall back to a `"(no content)"` placeholder when content is empty or nullish
 * 2. Render a whole XML-style envelope verbatim, preserving its line structure
 * 3. Render anything else as Markdown
 *
 * Raw HTML is never honoured as HTML on any path. react-markdown rewrites `raw`
 * nodes into text nodes — this app has no `rehype-raw`, which is the only thing
 * that could turn them into elements — and the verbatim path passes a plain
 * string child that React escapes. `skipHtml` is deliberately NOT set: it
 * *deletes* raw nodes rather than escaping them, which silently erased every
 * envelope message in what is an audit view.
 *
 * @param {{ content: string | null | undefined }} props
 * @param {string | null | undefined} props.content - The raw message text to render.
 */
function MessageContent({ content }: { content: string | null | undefined }) {
  const trimmed = (content ?? '').trim();
  if (!trimmed) {
    return <MessageBubble>(no content)</MessageBubble>;
  }
  if (WHOLE_ENVELOPE.test(trimmed)) {
    // `data-envelope` is the only handle a test has on this branch: the Linaria
    // class is stubbed to an ordinal in jsdom, and the Markdown path happens to
    // produce the same textContent for a single HTML block — so without this
    // marker the whole branch can be deleted with every test still green.
    return <EnvelopeBubble data-envelope="true">{trimmed}</EnvelopeBubble>;
  }
  return (
    <MessageBubble>
      <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{trimmed}</ReactMarkdown>
    </MessageBubble>
  );
}

/**
 * Renders a user-role message event with a labelled avatar and Markdown content.
 * @param {{ event: AgentMinerEvent }} props
 * @param {AgentMinerEvent} props.event - The user message event to display.
 */
function UserMessageBlock({ event }: { event: AgentMinerEvent }) {
  return (
    <Row>
      <Avatar bg={inkDarkest} fg="#fff">
        U
      </Avatar>
      <Body>
        <RoleLabel>User</RoleLabel>
        <MessageContent content={event.content} />
      </Body>
    </Row>
  );
}

/**
 * Narrows an unknown value to `number`, returning `undefined` for any other type.
 * @param {unknown} value - The value to check.
 * @returns {number | undefined} The original value if it is a `number`, otherwise `undefined`.
 */
const toNum = (value: unknown): number | undefined => (typeof value === 'number' ? value : undefined);

/**
 * Narrows an unvalidated JSON payload to a plain keyed object, or null.
 *
 * `tool_input` / `tool_output` arrive from a JSONField that stores objects,
 * bare strings and arrays, so any key access or `Object.entries` on them needs
 * this first. Arrays are excluded deliberately: they index numerically, which
 * is how a string payload came to render as `0="x", 1="x", …`.
 *
 * @param {unknown} value - A raw payload from the wire.
 * @returns {Record<string, unknown> | null} The value as a keyed object, or null.
 */
const asKeyedObject = (value: unknown): Record<string, unknown> | null =>
  value != null && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

/**
 * Renders an assistant-role message event with a labelled avatar, optional token counts, and Markdown content.
 * @param {{ event: AgentMinerEvent }} props
 * @param {AgentMinerEvent} props.event - The assistant message event to display.
 */
function AssistantMessageBlock({ event }: { event: AgentMinerEvent }) {
  const usage = asKeyedObject(event.tool_input);
  const inTokens = toNum(usage?.input_tokens);
  const outTokens = toNum(usage?.output_tokens);
  return (
    <Row>
      <Avatar bg={secondaryBase} fg="#fff">
        A
      </Avatar>
      <Body>
        <RoleLabel>
          <span>Assistant</span>
          {inTokens != null && outTokens != null && (
            <RoleMeta>
              {fmtNum(inTokens)} in · {fmtNum(outTokens)} out
            </RoleMeta>
          )}
        </RoleLabel>
        <MessageContent content={event.content} />
      </Body>
    </Row>
  );
}

/**
 * Renders a subagent-role message event with a custom label, a square avatar, and Markdown content.
 * @param {{ event: AgentMinerEvent; label: string }} props
 * @param {AgentMinerEvent} props.event - The subagent message event to display.
 * @param {string} props.label - The display label shown in place of a generic role name (e.g. the subagent's name).
 */
function SubagentMessageBlock({ event, label }: { event: AgentMinerEvent; label: string }) {
  return (
    <Row>
      <Avatar bg={secondaryDarkest} fg="#fff" square>
        S
      </Avatar>
      <Body>
        <RoleLabel>{label}</RoleLabel>
        <MessageContent content={event.content} />
      </Body>
    </Row>
  );
}

/**
 * Renders a collapsible thinking block showing the model's internal reasoning.
 * The content is hidden by default and toggled via a chevron button; an optional duration is shown in the header.
 * Reasoning that the runtime only exposes as an encrypted signature (e.g. Claude Code / Opus extended
 * thinking) has no readable text — those are flagged via `metadata.encrypted` and get a lock badge.
 * @param {{ event: AgentMinerEvent }} props
 * @param {AgentMinerEvent} props.event - The thinking event, expected to carry `content` and optionally `duration_ms` / `metadata.encrypted`.
 */
function ThinkingBlock({ event }: { event: AgentMinerEvent }) {
  const [open, setOpen] = useState(false);
  const isEncrypted = event.metadata?.encrypted === true;
  return (
    <Row>
      <Avatar bg={secondaryLightest} fg={secondaryDarkest}>
        {isEncrypted ? '🔒' : '…'}
      </Avatar>
      <Body>
        <ThinkingToggle type="button" onClick={() => setOpen((prev) => !prev)} aria-expanded={open}>
          <ThinkingChevron open={open}>▶</ThinkingChevron>
          Thinking
          {isEncrypted && <EncryptedBadge>encrypted</EncryptedBadge>}
          {event.duration_ms != null && <RoleMeta>· {fmtDuration(event.duration_ms)}</RoleMeta>}
        </ThinkingToggle>
        {open && (
          <ThinkingContent>
            {isEncrypted
              ? 'The runtime does not expose this reasoning text — only that the model reasoned here.'
              : (event.content ?? '(no thinking content)')}
          </ThinkingContent>
        )}
      </Body>
    </Row>
  );
}

const Row = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const IndentRow = styled(Row)`
  padding-left: 30px;
`;

const Avatar = styled.div<{ bg: string; fg: string; square?: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: ${(props) => (props.square ? '4px' : '99px')};
  background: ${(props) => props.bg};
  color: ${(props) => props.fg};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font: 600 10px/1 'Figtree', sans-serif;
  flex-shrink: 0;
  margin-top: 2px;
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const RoleLabel = styled.div`
  font: 600 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
  margin-bottom: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  display: flex;
  gap: 8px;
`;

const RoleMeta = styled.span`
  color: ${inkLighter};
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
`;

const EncryptedBadge = styled.span`
  margin-left: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  background: ${secondaryLightest};
  color: ${secondaryDarkest};
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
`;

const BodyText = styled.div`
  font: 400 13px/1.5 'Figtree', sans-serif;
  color: ${inkDarkest};
  white-space: pre-wrap;
  word-break: break-word;
`;

const MessageBubble = styled.div`
  padding: 10px 12px;
  background: ${skyLightest};
  border: 1px solid ${skyLight};
  border-radius: 8px;
  font: 400 13px/1.55 'Figtree', sans-serif;
  color: ${inkDarkest};
  word-break: break-word;

  /* Markdown primitives — keep tight vertical rhythm inside the bubble. */
  > :first-child {
    margin-top: 0;
  }
  > :last-child {
    margin-bottom: 0;
  }
  p {
    margin: 0 0 8px;
  }
  ul,
  ol {
    margin: 0 0 8px;
    padding-left: 20px;
  }
  li {
    margin-bottom: 2px;
  }
  h1,
  h2,
  h3,
  h4 {
    font-weight: 600;
    margin: 8px 0 4px;
  }
  h1 {
    font-size: 15px;
  }
  h2 {
    font-size: 14px;
  }
  h3,
  h4 {
    font-size: 13px;
  }
  code {
    font-family: 'SF Mono', ui-monospace, Menlo, monospace;
    font-size: 12px;
    background: ${secondaryLightest};
    border: 1px solid ${skyLight};
    border-radius: 3px;
    padding: 0 4px;
    color: ${secondaryDarkest};
  }
  pre {
    background: ${skyLight};
    border: 1px solid ${skyBase};
    border-radius: 6px;
    padding: 10px 12px;
    overflow-x: auto;
    margin: 8px 0;
  }
  pre code {
    background: transparent;
    border: none;
    padding: 0;
    color: ${inkDarkest};
  }
  blockquote {
    margin: 8px 0;
    padding: 4px 10px;
    border-left: 2px solid ${secondaryBase};
    color: ${inkLight};
  }
  a {
    color: ${secondaryDarkest};
  }
  table {
    border-collapse: collapse;
    margin: 8px 0;
  }
  th,
  td {
    border: 1px solid ${skyLight};
    padding: 4px 8px;
    text-align: left;
  }
`;

/*
 * Bubble for a runtime-injected envelope, shown exactly as stored. `pre-wrap`
 * keeps the one-tag-per-line structure Markdown would collapse, and the
 * monospace face marks the text as machine output rather than typed prose.
 * The rule lives here rather than on MessageBubble on purpose — `pre-wrap`
 * there would leak Markdown source line breaks into every prose message.
 */
const EnvelopeBubble = styled(MessageBubble)`
  white-space: pre-wrap;
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  font-size: 12px;
  /*
   * Bounded because this path un-hid content that used to be dropped: a
   * local-command-stdout envelope carries whole command transcripts, and
   * nothing upstream clips it. Scrolls rather than truncates — this is an audit
   * view, so the tail has to stay reachable. (No backticks in this comment:
   * inside a Linaria template literal they terminate the CSS and the remainder
   * parses as JavaScript.)
   */
  max-height: 360px;
  overflow: auto;
`;

const ThinkingToggle = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  font: 600 11px/1 'Figtree', sans-serif;
  color: ${secondaryDarkest};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 4px;
`;

const ThinkingChevron = styled.span<{ open: boolean }>`
  display: inline-block;
  transform: ${(props) => (props.open ? 'rotate(90deg)' : 'none')};
  transition: transform 0.15s ease;
`;

const ThinkingContent = styled.div`
  font: 400 12px/1.5 'Figtree', sans-serif;
  color: ${inkLight};
  font-style: italic;
  padding: 6px 10px;
  background: ${secondaryLightest};
  border-radius: 6px;
  border-left: 2px solid ${secondaryBase};
`;

export {
  ARG_PREVIEW_MAX_LENGTH,
  asKeyedObject,
  clipSummaryValue,
  describeArgShape,
  AssistantMessageBlock,
  Avatar,
  Body,
  BodyText,
  formatJson,
  IndentRow,
  RoleLabel,
  Row,
  SubagentMessageBlock,
  summarizeRecord,
  ThinkingBlock,
  UserMessageBlock,
};
